"use client";
import { Suspense } from "react";
import Script from "next/script";
import I18nProvider from "./components/providers/I18nProvider";
import ConditionalAuthProviders from "./components/providers/ConditionalAuthProviders";
import AnalyticsProvider from "./components/providers/AnalyticsProvider";
import { SWRProvider } from "./components/providers/SWRProvider";
import { CurrencyProvider } from "./components/providers/CurrencyProvider";
import CookieConsentBanner from "./components/CookieConsentBanner";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import { GA_TRACKING_ID, FB_PIXEL_ID } from "@/app/lib/tracking";
import { CONSENT_KEY, googleConsentState } from "@/app/lib/consent";
import type { SupportedLocale } from "@/app/lib/i18n-config";
import type { Translations } from "@/app/utils/i18next";
import type { Currency } from "@/app/utils/currency-config";

interface ClientLayoutProps {
  children: React.ReactNode;
  /** Locale resolved on the server; client components render in it first. */
  locale: SupportedLocale;
  /** Currency the middleware chose for this request (x-currency). */
  currency: Currency;
  /** Translations for `locale`, so the client never bundles every locale. */
  translations: Translations;
}

export default function ClientLayout({
  children,
  locale,
  currency,
  translations,
}: ClientLayoutProps) {
  return (
    <>
      {/* Analytics: the fbq/gtag stubs are defined right away so calls queue
          as before. gtag.js loads once the page is idle; the Meta Pixel
          library only after cookie consent (CookieConsentBanner). */}
      <Script id="facebook-pixel" strategy="afterInteractive">
        {`
					!function(f,n)
					{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					n.queue=[]}(window);
					fbq('init', '${FB_PIXEL_ID}');
				`}
      </Script>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
        strategy="lazyOnload"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
					window.dataLayer = window.dataLayer || [];
					function gtag(){dataLayer.push(arguments);}
					// Consent Mode v2: must precede config. Denied until the banner says OK.
					var __consentGranted = false;
					try { __consentGranted = localStorage.getItem('${CONSENT_KEY}') === 'accepted'; } catch (e) {}
					gtag('consent', 'default', __consentGranted
						? ${JSON.stringify(googleConsentState(true))}
						: ${JSON.stringify(googleConsentState(false))});
					gtag('js', new Date());
					gtag('config', '${GA_TRACKING_ID}', {
						page_path: window.location.pathname + window.location.search,
						transport_type: 'beacon',
						send_page_view: false
					});
				`}
      </Script>
      {/* No page-wide Suspense: a boundary here hid the whole page behind a
          spinner until the last streamed byte, delaying LCP by seconds. */}
      <I18nProvider locale={locale} translations={translations}>
        <CurrencyProvider initialCurrency={currency}>
          <SWRProvider>
            <ConditionalAuthProviders>
              {/* useSearchParams needs a boundary on statically rendered pages */}
              <Suspense fallback={null}>
                <AnalyticsProvider />
              </Suspense>
              {/* First in the body so it paints with the first bytes (it is
                  the mobile LCP element); a fixed overlay, so order is unseen */}
              <CookieConsentBanner />
              <EmailVerificationBanner />
              <main>{children}</main>
            </ConditionalAuthProviders>
          </SWRProvider>
        </CurrencyProvider>
      </I18nProvider>
    </>
  );
}
